import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Typography,
  Container,
  Menu,
  MenuItem,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItemButton,
  Collapse
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
// import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BlogIcon from '../assets/icons/blog.svg';
import BetaIcon from './common/SVGIcons/BetaIcon';
import MenuIcon from '@mui/icons-material/Menu';
import logo from '../../public/icon.png';
import useAuthToken from '../hooks/useAuthToken';
import { signInWithRedirect } from 'aws-amplify/auth';
import { handleSignOut } from '../handlers/signOut';
import FaqsIcon from '../assets/icons/Faqs.svg';
import DiscordIcon from '../assets/icons/discord.svg';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ArticleIcon from '@mui/icons-material/Article';
import CloseIcon from '../landingPage/assets/icons/CloseIcon';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PUBLIC_SIGN_IN_ENABLED } from '../constants/publicAuth';

const GlobalHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigation = useNavigate();
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const { isAuthenticated, isLoading: tokenLoading } = useAuthToken();
  const location = useLocation();

  const NavigateHome = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigation('/');
    }
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    if (!href.startsWith('#')) {
      navigation(href);
      return;
    }
    if (location.pathname === '/') {
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigation(`/${href}`);
    }
  };

  const handleMobileNavClick = href => {
    setOpen(false); // Close the drawer
    setTimeout(() => {
      if (!href.startsWith('#')) {
        navigation(href);
        return;
      }
      if (location.pathname === '/') {
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigation(`/${href}`);
      }
    }, 150);
  };

  const handleSignIn = async () => {
    if (isLoading || tokenLoading) return;
    try {
      setLoading(true);
      if (isAuthenticated) await handleSignOut();
      await signInWithRedirect({ provider: 'Google' });
    } catch (error) {
      console.error('signUp error', error);
    } finally {
      setLoading(false);
    }
  };
  const handleHelpMenuOpen = event => {
    setHelpAnchorEl(event.currentTarget);
  };

  const handleHelpMenuClose = () => {
    setHelpAnchorEl(null);
  };

  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleHelpToggle = () => {
    setHelpOpen(prev => !prev);
  };

  const toggleDrawer = (state: boolean) => () => {
    setOpen(state);
  };

  const navLinks = [
    { text: 'Agency', href: '/' },
    { text: 'UGC Creators', href: '/ugc-creators' },
    { text: 'Brands', href: '/brands' },
  ];

  return (
    <AppBar
      position="static"
      sx={{ backgroundColor: 'black', boxShadow: 0, height: { xs: '85px', sm: '68px', md: '58px' }, display: 'flex', alignItems: 'center' }}
    >
      <Container maxWidth="xl">
        <Toolbar
          disableGutters
          sx={{
            justifyContent: 'space-between',
            fontFamily: 'Inter',
            minHeight: '58px !important',
            paddingx: '1px',
            paddingy: '1px',
            pb: {
              xs: '16px',
              sm: '16px',
              md: '16px'
            },
            pt: {
              xs: '8px',
              sm: '8px',
              md: '8px'
            }
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={NavigateHome}>
            <Box component="img" src={logo} alt="Company Logo" />
            <Typography
              fontFamily="Inter"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '100%',
                letterSpacing: '0px',
                textAlign: 'center',
                verticalAlign: 'middle',
                marginLeft: '9px'
              }}
            >
              <BetaIcon />
            </Typography>
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {PUBLIC_SIGN_IN_ENABLED && (
                <Button
                  onClick={handleSignIn}
                  sx={{
                    height: '32px',
                    px: 2,
                    borderRadius: '170px',
                    boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
                    fontWeight: 'bold',
                    fontSize: '13px',
                    background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
                    color: 'white',
                    textTransform: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Sign Up
                </Button>
              )}

              <IconButton size="large" edge="end" color="inherit" onClick={toggleDrawer(true)}>
                <MenuIcon />
              </IconButton>

              <Drawer anchor="left" open={open} onClose={toggleDrawer(false)} PaperProps={{ sx: { backgroundColor: '#000', width: '100%', p: 3 } }}>
                {/* Logo + Close */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="img" src={logo} alt="Company Logo" sx={{ mr: 1, height: 32 }} />
                    <Typography fontFamily="Inter" fontWeight={600} fontSize="20px" color="#fff">
                      Saagas
                    </Typography>
                  </Box>
                  <IconButton onClick={toggleDrawer(false)} sx={{ color: '#fff', paddingRight: 0.5 }}>
                    <CloseIcon height={25} width={25} />
                  </IconButton>
                </Box>

                <List>
                  {/* Dynamic navLinks */}
                  {navLinks.map(link => (
                    <Box display="flex" justifyContent="space-between" alignItems="center" key={link.text} sx={{ borderBottom: '1px solid #393939' }}>
                      <ListItemButton
                        key={link.text}
                        onClick={() => handleMobileNavClick(link.href)}
                        sx={{
                          color: '#1BB3CB',
                          fontFamily: 'Inter',
                          padding: '15px 0px'
                        }}
                      >
                        {link.text}
                      </ListItemButton>
                    </Box>
                  ))}

                  {/* Pricing */}
                  <ListItemButton
                    onClick={() => handleMobileNavClick('#pricing')}
                    sx={{
                      color: '#A8A8A8',
                      borderBottom: '1px solid #393939',
                      mt: 3,
                      fontFamily: 'Inter',
                      padding: '15px 0px'
                    }}
                  >
                    <AttachMoneyIcon sx={{ width: 20, height: 20, mr: 1 }} />
                    Pricing
                  </ListItemButton>

                  {/* Blog */}
                  <ListItemButton
                    component={Link}
                    to="/blog"
                    onClick={toggleDrawer(false)}
                    sx={{
                      color: '#A8A8A8',
                      borderBottom: '1px solid #393939',
                      fontFamily: 'Inter',
                      padding: '15px 0px'
                    }}
                  >
                    <Box component="img" src={BlogIcon} sx={{ width: 20, height: 20, mr: 1 }} />
                    Blog
                  </ListItemButton>

                  {/* Help with Dropdown */}
                  <ListItemButton
                    onClick={handleHelpToggle}
                    sx={{
                      color: '#A8A8A8',
                      borderBottom: '1px solid #393939',
                      fontFamily: 'Inter',
                      padding: '15px 0px'
                    }}
                  >
                    <Box component="img" src={FaqsIcon} sx={{ width: 20, height: 20, mr: 1 }} />
                    Help
                    <KeyboardArrowDownIcon sx={{ color: '#747474', ml: 'auto' }} />
                  </ListItemButton>

                  <Collapse in={helpOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ pl: 4 }}>
                      <ListItemButton onClick={() => handleMobileNavClick('#faq')} sx={{ borderBottom: '1px solid #393939' }}>
                        <Typography color="#A8A8A8" fontFamily="Inter">
                          FAQs
                        </Typography>
                      </ListItemButton>
                      <ListItemButton component={Link} to="/docs" onClick={toggleDrawer(false)} sx={{ borderBottom: '1px solid #393939' }}>
                        <Typography color="#A8A8A8" fontFamily="Inter">
                          Docs
                        </Typography>
                      </ListItemButton>
                      <ListItemButton component={Link} to="/discord" onClick={toggleDrawer(false)} sx={{ borderBottom: '1px solid #393939' }}>
                        <Typography color="#A8A8A8" fontFamily="Inter">
                          Discord
                        </Typography>
                      </ListItemButton>
                      <ListItemButton component={Link} to="/contact-us" onClick={toggleDrawer(false)} sx={{ borderBottom: '1px solid #393939' }}>
                        <Typography color="#A8A8A8" fontFamily="Inter">
                          Contact Us
                        </Typography>
                      </ListItemButton>
                    </Box>
                  </Collapse>
                </List>

                {PUBLIC_SIGN_IN_ENABLED && (
                  <>
                    <Button
                      onClick={handleSignIn}
                      sx={{
                        width: '100%',
                        // height: '32px',
                        borderRadius: '170px',
                        // boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        border: '1px solid #1BB3CB',
                        // background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
                        color: 'white',
                        textTransform: 'none',
                        mt: 3,
                        padding: '12px 0'
                      }}
                    >
                      Sign In
                    </Button>
                    <Button
                      onClick={handleSignIn}
                      sx={{
                        width: '100%',
                        // height: '32px',
                        borderRadius: '170px',
                        boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
                        color: 'white',
                        textTransform: 'none',
                        mt: 3,
                        padding: '12px 0'
                      }}
                    >
                      Sign Up
                    </Button>
                  </>
                )}
              </Drawer>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {navLinks.slice(0, 1).map(link => (
                    <Button
                      key={link.text}
                      href={link.href}
                      onClick={e => handleNavClick(e, link.href)}
                      sx={{
                        color: ' #1BB3CB',
                        fontSize: '14px',
                        fontWeight: 500,
                        mx: 1,
                        textTransform: 'none',
                        borderBottom: location.pathname === link.href ? '1px solid #1BB3CB' : 'none',
                        paddingBottom: '4px',
                        borderRadius: 0,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {link.text}
                    </Button>
                  ))}
                  <Box sx={{ mx: 1, color: '#666', display: 'inline' }}>|</Box>
                  {navLinks.slice(1).map(link => (
                    <Button
                      key={link.text}
                      href={link.href}
                      onClick={e => handleNavClick(e, link.href)}
                      sx={{
                        color: ' #1BB3CB',
                        fontSize: '14px',
                        fontWeight: 500,
                        mx: 1,
                        textTransform: 'none',
                        borderBottom: location.pathname === link.href ? '1px solid #1BB3CB' : 'none',
                        paddingBottom: '4px', // Adjust spacing for the underline
                        borderRadius: 0,// Makes the underline sharp instead of rounded
                        whiteSpace: 'nowrap'
                        
                      }}
                    >
                      {link.text}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button
                    href="#pricing"
                    onClick={e => handleNavClick(e, '#pricing')}
                    sx={{
                      color: '#eee',
                      fontSize: '14px',
                      fontWeight: 500,
                      textTransform: 'none'
                    }}
                  >
                    Pricing
                  </Button>
                  <Button
                    component="a"
                    href="/blog"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#eee',
                      textTransform: 'none'
                    }}
                  >
                    Blog
                  </Button>
                  <Button
                    endIcon={<KeyboardArrowDownIcon />}
                    onClick={handleHelpMenuOpen}
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#eee',
                      textTransform: 'none'
                    }}
                  >
                    Help
                  </Button>
                  <Menu
                    sx={{
                      mt: '30px',
                      '& .MuiMenu-paper': {
                        backgroundColor: '#0D0D0D',
                        color: '#FFFFFF',
                        borderRadius: '10px',
                        border: '1px solid #413D3D'
                      },
                      '& .MuiMenuItem-root': {
                        padding: '10px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        fontSize: '14px',
                        fontWeight: 500
                      },
                      '& svg': {
                        width: '18px',
                        height: '18px'
                      }
                    }}
                    anchorEl={helpAnchorEl}
                    open={Boolean(helpAnchorEl)}
                    onClose={handleHelpMenuClose}
                  >
                    <MenuItem
                      onClick={e => { handleHelpMenuClose(); handleNavClick(e, '#faq'); }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <img src={FaqsIcon} alt="FAQs" />
                      FAQs
                    </MenuItem>
                    <MenuItem
                      component="a"
                      href="https://saagasai.mintlify.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleHelpMenuClose}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'inherit', textDecoration: 'none' }}
                    >
                      <ArticleIcon />
                      Docs
                    </MenuItem>
                    <MenuItem
                      component="a"
                      href="https://t.co/401XPwdXQx"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={handleHelpMenuClose}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'inherit', textDecoration: 'none' }}
                    >
                      <img src={DiscordIcon} alt="Discord" />
                      Discord
                    </MenuItem>
                    <MenuItem
                      component="a"
                      href="mailto:support@saagas.ai"
                      onClick={handleHelpMenuClose}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'inherit', textDecoration: 'none' }}
                    >
                      <MailOutlineIcon sx={{ fontSize: 18 }} />
                      Contact Us
                    </MenuItem>
                  </Menu>
                  {PUBLIC_SIGN_IN_ENABLED && (
                    <>
                      <Button
                        sx={{
                          fontSize: '14px',
                          fontWeight: 500,
                          color: '#eee',
                          textTransform: 'none'
                        }}
                        onClick={handleSignIn}
                      >
                        Sign In
                      </Button>
                      <Button
                        onClick={handleSignIn}
                        sx={{
                          padding: '8px 18px',
                          borderRadius: '170px',
                          gap: '10px',
                          boxShadow: '0px -4px 50px 0px rgba(11,87,208,0.68)',
                          color: 'white',
                          textAlign: 'center',
                          fontFamily: 'Inter',
                          fontWeight: 500,
                          fontSize: '14px',
                          lineHeight: '100%',
                          letterSpacing: '0px',
                          display: 'flex',
                          alignItems: 'center',
                          textTransform: 'none',
                          justifyContent: 'center',
                          whiteSpace: 'nowrap',
                          background: 'linear-gradient(163deg, rgb(12, 174, 207) -29.1%, rgb(88, 15, 107) 96.45%)',
                          '&:hover': {
                            background: 'linear-gradient(163deg, rgb(12, 160, 200) -29.1%, rgb(80, 10, 100) 96.45%)'
                          }
                        }}
                      >
                        Sign Up
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default GlobalHeader;
